import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'react-toastify';
import { changePassword } from '../../services/api';
import {
  ACCOUNT_CARD_SX,
  ACCOUNT_PAGE_BG,
  AccountHomeButton,
  AccountSettingsButton,
} from '../common/AccountPageNavigation';

const PRIMARY_BUTTON_SX = {
  minHeight: 44,
  px: 3,
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 700,
  background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
  boxShadow: '0 10px 24px -8px rgba(8, 145, 178, 0.45)',
  '&:hover': {
    background: 'linear-gradient(135deg, #0e7490 0%, #155e75 100%)',
  },
};

const ChangePasswordPage = () => {
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (form.newPassword !== form.confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    if (form.newPassword === form.currentPassword) {
      setError('A nova senha deve ser diferente da senha atual.');
      return;
    }

    setSaving(true);
    try {
      await changePassword({
        senhaAtual: form.currentPassword,
        novaSenha: form.newPassword,
      });
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Senha alterada com sucesso.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Não foi possível alterar a senha.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: ACCOUNT_PAGE_BG, py: { xs: 4, md: 6 } }}>
      <AccountSettingsButton />
      <Box sx={{ maxWidth: 1150, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <AccountHomeButton />

          <Card sx={{ ...ACCOUNT_CARD_SX, position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                inset: '0 0 auto 0',
                height: 4,
                background: 'linear-gradient(90deg, #06b6d4 0%, #8b5cf6 50%, #f59e0b 100%)',
              }}
            />
            <CardContent sx={{ p: { xs: 2.5, md: 3 }, pt: { xs: 3, md: 3.5 } }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '16px',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    color: '#0891b2',
                    background: 'linear-gradient(135deg, rgba(8,145,178,.14), rgba(124,58,237,.10))',
                    border: '1px solid rgba(8,145,178,.18)',
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 27 }} />
                </Box>
                <Box>
                  <Typography component="h1" variant="h5" fontWeight={700} sx={{ color: '#0f172a', letterSpacing: '-0.02em' }}>
                    Alterar senha
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.4 }}>
                    Confirme sua senha atual e defina uma nova senha de acesso.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card component="form" onSubmit={handleSubmit} sx={ACCOUNT_CARD_SX}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 }, '&:last-child': { pb: { xs: 2.5, md: 3 } } }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0f172a' }}>
                    Segurança da conta
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
                    A alteração será aplicada ao usuário conectado.
                  </Typography>
                </Box>

                {error && <Alert severity="error">{error}</Alert>}

                <TextField
                  fullWidth
                  required
                  label="Senha atual"
                  name="currentPassword"
                  type="password"
                  autoComplete="current-password"
                  value={form.currentPassword}
                  onChange={handleChange}
                  disabled={saving}
                />
                <TextField
                  fullWidth
                  required
                  label="Nova senha"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  value={form.newPassword}
                  onChange={handleChange}
                  disabled={saving}
                />
                <TextField
                  fullWidth
                  required
                  label="Confirmar nova senha"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  disabled={saving}
                />

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    disabled={saving}
                    sx={PRIMARY_BUTTON_SX}
                  >
                    {saving ? 'Salvando...' : 'Salvar senha'}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </Box>
  );
};

export default ChangePasswordPage;
